// @givmo/app-admin — dashboard header + actions.
//
// The page title and the actions slot. Its own file because the actions slot is where EP-WEB7's Export
// menu returns, so the section that grows next does not collide with the rest of the dashboard.

import { useNavigate } from '@tanstack/react-router';
import { Button, Icon, Menu, PageHeader } from '@givmo/design-system';
import { Download, FileSpreadsheet, FileText } from 'lucide-react';
import { useEmployerModulePaths } from '../../../lib/module-paths';
import { GiveEntryButton } from '../../give/give-entry-button';

export function DashboardHeader() {
  const navigate = useNavigate();
  const paths = useEmployerModulePaths();

  // Every item NAVIGATES to /reports rather than downloading from here. Deliberate: the recap and the
  // four CSVs live on one destination, and firing a download from the dashboard would drop an artifact
  // somewhere the admin cannot find again or re-run for a different period.
  const goToReports = (hash: string) => () => void navigate({ to: paths.reports, hash });

  return (
    <PageHeader
      title="Dashboard"
      subtitle="Your company's donation-matching program at a glance."
      actions={
        <div className="flex items-center gap-8">
          {/* EP-WEB1: DARK-gated direct-giving entry — renders nothing until the switch flips. */}
          <GiveEntryButton />
          {/* EP-WEB7 restores Export. Wave 0 REMOVED rather than hid the old button because it had no
              onClick — the decorative control the mission bar names by name. It is back only because
              there is something real behind it now: a recap built from live reads plus four working
              CSVs. Restoring it as a MENU rather than a button is what lets it name its destinations
              instead of implying one nonexistent "export everything" action. */}
          <Menu.Root>
            <Menu.Trigger
              testId="dashboard-export"
              render={
                <Button
                  appearance="saas"
                  tone="secondary"
                  leadingIcon={<Icon icon={Download} size={16} aria-hidden />}
                />
              }
            >
              Export
            </Menu.Trigger>
            <Menu.Content saas side="bottom" align="end">
              <Menu.Item
                saas
                testId="dashboard-export-recap"
                onClick={goToReports('recap')}
                startIcon={<Icon icon={FileText} size={16} className="text-icon" aria-hidden />}
              >
                Year recap
              </Menu.Item>
              <Menu.Item
                saas
                testId="dashboard-export-csvs"
                onClick={goToReports('exports')}
                startIcon={
                  <Icon icon={FileSpreadsheet} size={16} className="text-icon" aria-hidden />
                }
              >
                Spreadsheet exports
              </Menu.Item>
            </Menu.Content>
          </Menu.Root>
        </div>
      }
    />
  );
}
